/**
 * Brand imagery configuration.
 * 
 * Maps brands to category types for image selection, and provides
 * curated image sets per product category for brand pages.
 */

// Product category image pools — curated luxury editorial images
const IMAGE_POOLS = {
  handbags: [
    'https://images.unsplash.com/photo-1584917865442-de89df76afd3?w=600&q=80',
    'https://images.unsplash.com/photo-1590874103328-eac38a683ce7?w=600&q=80',
    'https://images.unsplash.com/photo-1705909237050-7a7625b47fac?w=600&q=80',
    'https://images.unsplash.com/photo-1540749046540-b7d8f98c7e4c?w=600&q=80',
    'https://images.unsplash.com/photo-1695150721187-7e23cc8ca591?w=600&q=80',
    'https://images.unsplash.com/photo-1630701875820-1250d036825d?w=600&q=80',
  ],
  watches: [
    'https://images.unsplash.com/photo-1524592094714-0f0654e20314?w=600&q=80',
    'https://images.unsplash.com/photo-1587836374828-4dbafa94cf0e?w=600&q=80',
    'https://images.unsplash.com/photo-1661716978706-8e42868bb0b5?w=600&q=80',
    'https://images.unsplash.com/photo-1607996108811-94137abf5ccf?w=600&q=80',
    'https://images.unsplash.com/photo-1680810897186-372717262131?w=600&q=80',
    'https://images.unsplash.com/photo-1708551452682-3f969fb4b38f?w=600&q=80',
  ],
  jewelry: [
    'https://images.unsplash.com/photo-1535632066927-ab7c9ab60908?w=600&q=80',
    'https://images.unsplash.com/photo-1515562141589-67f0d569b6fc?w=600&q=80',
    'https://images.unsplash.com/photo-1611591437281-460bfbe1220a?w=600&q=80',
    'https://images.unsplash.com/photo-1605100804763-247f67b3557e?w=600&q=80',
    'https://images.unsplash.com/photo-1573408301185-9146fe634ad0?w=600&q=80',
    'https://images.unsplash.com/photo-1724937721110-862ebf74ed7a?w=600&q=80',
  ],
  shoes: [
    'https://images.unsplash.com/photo-1543163521-1bf539c55dd2?w=600&q=80',
    'https://images.unsplash.com/photo-1595950653106-6c9ebd614d3a?w=600&q=80',
    'https://images.unsplash.com/photo-1618274158630-bc47a614b3a5?w=600&q=80',
    'https://images.unsplash.com/photo-1600185365483-26d7a4cc7519?w=600&q=80',
    'https://images.unsplash.com/photo-1560769629-975ec94e6a86?w=600&q=80',
    'https://images.unsplash.com/photo-1646799216965-fd768536d596?w=600&q=80',
  ],
  clothing: [
    'https://images.unsplash.com/photo-1558171813-01eda89a5f99?w=600&q=80',
    'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=600&q=80',
    'https://images.unsplash.com/photo-1523381294911-8d3cead13475?w=600&q=80',
    'https://images.unsplash.com/photo-1551488831-00ddcb6c6bd3?w=600&q=80',
    'https://images.unsplash.com/photo-1556905055-8f358a7a47b2?w=600&q=80',
    'https://images.unsplash.com/photo-1620799140408-edc6dcb6d633?w=600&q=80',
  ],
  accessories: [
    'https://images.unsplash.com/photo-1509281373149-e957c6296406?w=600&q=80',
    'https://images.unsplash.com/photo-1606522754091-d86bbd9f4b30?w=600&q=80',
    'https://images.unsplash.com/photo-1617038220319-276d3cfab638?w=600&q=80',
    'https://images.unsplash.com/photo-1526045431048-f857369baa09?w=600&q=80',
    'https://images.unsplash.com/photo-1547996160-81dfa63595aa?w=600&q=80',
    'https://images.unsplash.com/photo-1509048191080-d2984bad6ae5?w=600&q=80',
  ],
};

// Brand → primary product category mapping
const BRAND_CATEGORIES = {
  // Watch brands
  'rolex': 'watches', 'omega': 'watches', 'cartier': 'watches', 'tag-heuer': 'watches',
  'breitling': 'watches', 'patek-philippe': 'watches', 'audemars-piguet': 'watches',
  'iwc': 'watches', 'panerai': 'watches', 'chopard': 'watches', 'blancpain': 'watches',
  'bell-ross': 'watches', 'baume-mercier': 'watches', 'movado': 'watches',
  'zenith': 'watches', 'philippe-charriol': 'watches', 'mont-blanc': 'watches',
  // Jewelry brands
  'tiffany-co': 'jewelry', 'bulgari': 'jewelry', 'david-yurman': 'jewelry',
  'van-cleef-arpels': 'jewelry', 'john-hardy': 'jewelry', 'pandora': 'jewelry',
  // Shoe brands
  'christian-louboutin': 'shoes', 'jimmy-choo': 'shoes', 'manolo-blahnik': 'shoes',
  'giuseppe-zanotti': 'shoes', 'aquazzura': 'shoes', 'stuart-weitzman': 'shoes',
  'roger-vivier': 'shoes', 'brian-atwood': 'shoes', 'gianvito-rossi': 'shoes',
  'amina-muaddi': 'shoes', 'golden-goose': 'shoes', 'charlotte-olympia': 'shoes',
  'tods': 'shoes', 'john-lobb': 'shoes', 'ugg': 'shoes',
  'adidas': 'shoes', 'yeezy': 'shoes',
  // Clothing-primary brands
  'balmain': 'clothing', 'thom-browne': 'clothing', 'rick-owens': 'clothing',
  'raf-simons': 'clothing', 'vetements': 'clothing', 'supreme': 'clothing',
  'fear-of-god': 'clothing', 'amiri': 'clothing', 'dsquared2': 'clothing',
  'ralph-lauren': 'clothing', 'calvin-klein': 'clothing', 'escada': 'clothing',
  'kiton': 'clothing', 'brioni': 'clothing', 'isaia': 'clothing',
  'ermenegildo-zegna': 'clothing', 'max-mara': 'clothing', 'moncler': 'clothing',
  'canada-goose': 'clothing', 'belstaff': 'clothing', 'missoni': 'clothing',
  'kenzo': 'clothing', 'moschino': 'clothing', 'r13-jeans': 'clothing',
  'helmut-lang': 'clothing', 'st-john': 'clothing',
  'halston': 'clothing', 'philipp-plein': 'clothing',
  // Accessories
  'ray-ban': 'accessories', 'dunhill': 'accessories',
  // Default: handbags (most luxury brands are handbag-primary)
};

/**
 * Get the image set for a brand based on its slug.
 * Returns an array of image URLs.
 *
 * Slice 8.1: image resolution follows a fallback chain, so admins can swap the
 * pool without a redeploy:
 *   1. `overrides[slug]` (array) — CMS/settings-driven override for THIS brand
 *   2. `IMAGE_POOLS[category]` (default) — the curated Unsplash pool below
 * When self-hosted `cms/` keys are wired into the CMS, admins upload the images
 * and set `cms.brand_images[slug] = ["cms/handbag-1.jpg", ...]`; the frontend
 * resolves those keys via `/api/storage/resolve` before rendering.
 */
export function getBrandImages(slug, overrides = {}) {
  if (overrides && Array.isArray(overrides[slug]) && overrides[slug].length) {
    return overrides[slug];
  }
  const category = BRAND_CATEGORIES[slug] || 'handbags';
  const pool = IMAGE_POOLS[category] || IMAGE_POOLS.handbags;
  // Use slug hash to pick a deterministic subset so each brand looks different
  const hash = slug.split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  const start = hash % pool.length;
  return [
    pool[start % pool.length],
    pool[(start + 1) % pool.length],
    pool[(start + 2) % pool.length],
    pool[(start + 3) % pool.length],
  ];
}

/**
 * Get the hero image for a brand (single featured image).
 */
export function getBrandHeroImage(slug) {
  const images = getBrandImages(slug);
  return images[0];
}

/**
 * Get the product category label for a brand.
 */
export function getBrandCategory(slug) {
  const cat = BRAND_CATEGORIES[slug] || 'handbags';
  return cat.charAt(0).toUpperCase() + cat.slice(1);
}

export default { getBrandImages, getBrandHeroImage, getBrandCategory, IMAGE_POOLS };
